import { supabase } from './supabaseClient';
import { getFullUrl } from './siteConfig';

/** Pending payment created for SMS/email portal when Stripe pay link is not included. */
export const INVOICE_PORTAL_VIEW_REFERENCE = 'invoice-portal-view';

export function isInvoiceViewOnlyReference(reference: string | null | undefined): boolean {
  return reference === INVOICE_PORTAL_VIEW_REFERENCE;
}

export function buildPayInvoiceUrl(paymentToken: string, options?: { pdfOnly?: boolean }): string {
  const path = `/pay-invoice/${paymentToken}`;
  if (options?.pdfOnly) {
    return getFullUrl(`${path}?tab=pdf`);
  }
  return getFullUrl(path);
}

/** Reuse or create a pending payment row used only for the invoice portal (PDF tab, no Stripe). */
export async function ensureInvoicePortalPayment(params: {
  businessId: string;
  bookingId: string;
  bookingNumber: number;
  existingPayments: Array<{ id: string; status: string; reference?: string | null; payment_token?: string | null }>;
}): Promise<{ id: string; payment_token: string } | null> {
  const existing = params.existingPayments.find(
    (p) => p.status === 'pending' && p.reference === INVOICE_PORTAL_VIEW_REFERENCE,
  );
  if (existing?.payment_token) {
    return { id: existing.id, payment_token: existing.payment_token };
  }

  const paymentToken = crypto.randomUUID();
  const { data: created, error } = await supabase
    .from('payments')
    .insert({
      business_id: params.businessId,
      booking_id: params.bookingId,
      amount: 0,
      method: 'credit_card',
      status: 'pending',
      reference: INVOICE_PORTAL_VIEW_REFERENCE,
      notes: `Invoice portal for booking #${params.bookingNumber}`,
      payment_token: paymentToken,
      payment_type: 'final',
    })
    .select('id, payment_token')
    .single();

  if (error || !created?.payment_token) {
    console.error('Failed to create invoice portal payment:', error);
    return null;
  }

  return { id: created.id, payment_token: created.payment_token };
}

function base64ToPdfBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'application/pdf' });
}

export async function fetchPublicInvoicePdf(token: string): Promise<{
  blob: Blob;
  filename: string;
} | null> {
  const { data, error } = await supabase.functions.invoke('get-public-invoice-pdf', {
    body: { token },
  });

  if (error || !data?.base64) {
    console.error('Failed to load public invoice PDF:', error || data?.error);
    return null;
  }

  return {
    blob: base64ToPdfBlob(data.base64 as string),
    filename: (data.filename as string) || 'invoice.pdf',
  };
}

export interface PublicInvoiceSummary {
  hasInvoice: boolean;
  totalCents: number;
  amountPaidCents: number;
  amountDueCents: number;
  tipsCents: number;
  surchargeCents: number;
  status?: string;
  /** Standalone (booking-less) invoice extras. */
  isStandalone?: boolean;
  invoiceNumber?: string | null;
  customerName?: string | null;
}

/** Fetch the new-invoice-system money for a public payment link by token (anon-safe). */
export async function fetchPublicInvoiceSummary(token: string): Promise<PublicInvoiceSummary | null> {
  const { data, error } = await supabase.functions.invoke('get-public-invoice-summary', {
    body: { token },
  });

  if (error || !data || !data.hasInvoice) return null;

  return {
    hasInvoice: true,
    totalCents: Number(data.totalCents) || 0,
    amountPaidCents: Number(data.amountPaidCents) || 0,
    amountDueCents: Number(data.amountDueCents) || 0,
    tipsCents: Number(data.tipsCents) || 0,
    surchargeCents: Number(data.surchargeCents) || 0,
    status: data.status,
    isStandalone: !!data.isStandalone,
    invoiceNumber: data.invoiceNumber ?? null,
    customerName: data.customerName ?? null,
  };
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/** Public GET URL that returns PDF with Content-Disposition: attachment (mobile-friendly). */
export function getInvoicePdfAttachmentUrl(paymentToken: string): string | null {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!baseUrl || !anonKey) return null;
  const params = new URLSearchParams({
    token: paymentToken,
    apikey: anonKey,
  });
  return `${baseUrl}/functions/v1/get-public-invoice-pdf?${params.toString()}`;
}

/**
 * Save invoice PDF to device. Mobile browsers ignore <a download> for PDFs and open them inline instead.
 */
async function trySharePdfFile(file: File, filename: string): Promise<boolean> {
  if (typeof navigator.share !== 'function') return false;
  const sharePayload: ShareData = { files: [file], title: filename };
  if (navigator.canShare && !navigator.canShare(sharePayload)) return false;
  try {
    await navigator.share(sharePayload);
    return true;
  } catch (err) {
    if ((err as Error).name === 'AbortError') return true;
    return false;
  }
}

function triggerAttachmentDownload(attachmentUrl: string): void {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
  iframe.src = attachmentUrl;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 120_000);
}

/**
 * Save invoice PDF to device. Mobile browsers ignore <a download> for PDFs and open them inline instead.
 */
export async function downloadInvoicePdf(params: {
  token: string;
  blob: Blob;
  filename: string;
}): Promise<void> {
  const { token, blob, filename } = params;
  const file = new File([blob], filename, { type: 'application/pdf' });
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const attachmentUrl = getInvoicePdfAttachmentUrl(token);

  // iOS: only the share sheet reliably saves PDFs (blob/download opens the viewer)
  if (isIOS) {
    const shared = await trySharePdfFile(file, filename);
    if (shared) return;
    throw new Error('Share not available');
  }

  // Android: attachment response in a hidden iframe
  if (isAndroid && attachmentUrl) {
    triggerAttachmentDownload(attachmentUrl);
    return;
  }

  if (isMobileDevice()) {
    const shared = await trySharePdfFile(file, filename);
    if (shared) return;
    if (attachmentUrl) {
      triggerAttachmentDownload(attachmentUrl);
      return;
    }
  }

  // Desktop: same-origin blob download
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
}
